'use client';

import { useCallback, useEffect } from 'react';
import Router from 'next/router';
import { noTopSafeArea } from '@digitaz/bridge-sdk-client';
import { useShallow } from 'zustand/react/shallow';
import { useBodyClasses } from '@/hooks/useBodyClasses';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { initializeGoogleTagManager } from '@/utils/googleTagManager';
import { trackLiveInternetHit } from '@/utils/liveInternet';
import { CONFIG } from '@/constants/config';
import { useLoginUser } from '@/hooks/useLoginUser';
import { useFirstInteraction } from '@/hooks/useFirstInteraction';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { useNavigation } from '@/hooks/useNavigation';
import { createRouterReplace } from '@/bridge/utils/navigation';
import { useBackgroundBanners } from '@/components/Banners/Banners.hooks';

interface ClientWrapperProps {
  children: React.ReactNode;
  isSSRWebView?: boolean;
  accessToken?: string;
}

export function ClientWrapper({
  children,
  isSSRWebView,
  accessToken,
}: ClientWrapperProps) {
  useBodyClasses();
  useBackNavigation();
  useBackgroundBanners();
  const isLoginInProgress = useLoginUser(accessToken);
  const [canLoadBanners, setCanLoadBanners] = useBannerStore(
    useShallow(state => [state.canLoadBanners, state.setCanLoadBanners])
  );
  useNavigation({ skipQueries: isLoginInProgress });

  const handleFirstInteraction = useCallback(() => {
    setCanLoadBanners(true);
    trackLiveInternetHit();
  }, [setCanLoadBanners]);

  useFirstInteraction(handleFirstInteraction);

  useEffect(() => {
    Router.events.on('routeChangeComplete', trackLiveInternetHit);

    return () => {
      Router.events.off('routeChangeComplete', trackLiveInternetHit);
    };
  }, []);

  useEffect(() => {
    if (canLoadBanners) {
      initializeGoogleTagManager(CONFIG.gtmContainerId);
    }

    if (isSSRWebView) {
      noTopSafeArea('', createRouterReplace(Router));
      Router.events.on('beforeHistoryChange', noTopSafeArea);
    }

    if (!document.querySelector('script[src*="gpt.js"]') && canLoadBanners) {
      const script = document.createElement('script');
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (isSSRWebView) {
        Router.events.off('beforeHistoryChange', noTopSafeArea);
      }
    };
  }, [isSSRWebView, canLoadBanners]);

  if (isLoginInProgress) return null;

  return <>{children}</>;
}
